import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { Plus, AlertTriangle } from "lucide-react";
import { Modal } from "@/pages/Products";
import { toast } from "sonner";

export default function Inventory() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState(user?.restaurant_id || "");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ product_id: "", stock: 0, min_stock: 0 });

  const load = async () => {
    const [inv, p, r] = await Promise.all([
      api.get("/inventory", { params: restaurantId ? { restaurant_id: restaurantId } : {} }),
      api.get("/products"),
      api.get("/restaurants"),
    ]);
    setList(inv.data); setProducts(p.data); setRestaurants(r.data);
    if (!restaurantId && r.data[0]) setRestaurantId(user?.restaurant_id || r.data[0].id);
  };
  useEffect(() => { load(); }, [restaurantId]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/inventory", {
      restaurant_id: restaurantId,
      product_id: form.product_id,
      stock: parseFloat(form.stock),
      min_stock: parseFloat(form.min_stock),
    });
    toast.success("Voorraad bijgewerkt");
    setShowForm(false);
    setForm({ product_id: "", stock: 0, min_stock: 0 });
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="inventory-page">
      <PageHeader
        overline="Voorraad"
        title="Voorraadbeheer"
        description="Houd je actuele voorraad bij per restaurant."
        actions={
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="inventory-add-button">
            <Plus size={16} /> Voorraad bijwerken
          </button>
        }
      />

      <div className="mb-4">
        <label className="overline block mb-2">Restaurant</label>
        <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}
          className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 w-full sm:min-w-[260px] sm:w-auto"
          data-testid="inventory-restaurant-select"
        >
          <option value="">Alle</option>
          {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Product</th>
              <th className="px-4 py-3 overline">Eenheid</th>
              <th className="px-4 py-3 overline text-right">Op voorraad</th>
              <th className="px-4 py-3 overline text-right">Min. voorraad</th>
              <th className="px-4 py-3 overline">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {list.map((it) => {
              const low = it.stock <= it.min_stock;
              return (
                <tr key={it.id} className="hover:bg-zinc-50" data-testid={`inventory-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium">{it.product_name}</td>
                  <td className="px-4 py-3 text-zinc-600">{it.unit}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{it.stock}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{it.min_stock}</td>
                  <td className="px-4 py-3">
                    {low ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider px-2 py-1 border border-red-200 bg-red-50 text-red-700 rounded">
                        <AlertTriangle size={11} /> Laag
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded">
                        Voldoende
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-zinc-500">Nog geen voorraad geregistreerd.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title="Voorraad bijwerken">
          <form onSubmit={submit} className="space-y-3" data-testid="inventory-form">
            <div>
              <label className="overline block mb-1.5">Product</label>
              <select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="form-input" data-testid="inv-product">
                <option value="">— Kies —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1.5">Voorraad</label>
                <input type="number" step="0.01" required value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="form-input" data-testid="inv-stock" />
              </div>
              <div>
                <label className="overline block mb-1.5">Min. voorraad</label>
                <input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="form-input" data-testid="inv-min" />
              </div>
            </div>
            <button type="submit" className="w-full h-10 bg-[#5BC044] text-white rounded-md text-sm font-semibold" data-testid="inv-submit">Opslaan</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
